/**
 * Ağ Hatası Logger'ı — "sadece Windows'ta timeout" teşhisi için.
 *
 * NE YAPAR
 *   Her isteğin başlangıç anını damgalar; istek hata alınca (veya anormal yavaş
 *   tamamlanınca) şunları tek satırda konsola basar ve halkasal tampona yazar:
 *     geçen süre · yapılandırılmış timeout · error.code · error.message ·
 *     endpoint (method + url + params) · HTTP durum · tarayıcı/işletim sistemi (UA) ·
 *     navigator.onLine · build sürümü
 *
 * NEDEN AYRI DOSYA
 *   api.js'in kendi akışı (401 → refresh → retry) karışmasın; logger her yerden
 *   (ileride fetch/EventSource sarmalayıcıları) çağrılabilsin.
 *
 * UZAKTAKİ MAKİNEDEN KANIT ALMA (kullanıcıya söylenecek komutlar — F12 → Console)
 *   __vertexNet.dump()    → son hataları tablo olarak göster
 *   __vertexNet.text()    → tek metin blok (WhatsApp/mail ile gönderilebilir)
 *   __vertexNet.copy()    → aynı metni panoya kopyala
 *   __vertexNet.enable()  → ayrıntılı mod: HTTP 4xx/5xx hataları da ekrana toast olarak düşer
 *   __vertexNet.clear()   → tamponu boşalt
 *
 * GİZLİLİK: istek gövdesi ve header'lar (Authorization/token) ASLA loglanmaz;
 * yalnız method, yol, query parametreleri ve hata meta verisi tutulur.
 */
import { toast } from '../stores/uiStore'
import { apiErrorMessage } from './apiError'

const BUFFER_MAX = 50          // tamponda tutulan kayıt sayısı
const SS_KEY = 'vertex_netlog' // sessionStorage anahtarı (sekme yenilense de kanıt kalsın)
const SLOW_MS = 10000          // bu süreyi aşan BAŞARILI istek de "slow" olarak kaydedilir
const TOAST_DEDUPE_MS = 15000  // aynı endpoint + aynı hata türü için toast tekrarı engeli

/** Header/sidebar global poll'ları — timeout toast'u kullanıcıyı yanıltır. */
const BACKGROUND_POLL_RE = /\/(approvals\/count|notifications\/?$|chat\/(channels|unread-summary)|quotes\/meta\/customer-commercial)\b/i

function isBackgroundPollEndpoint(endpoint) {
  return BACKGROUND_POLL_RE.test(String(endpoint || ''))
}

const now = () =>
  (typeof performance !== 'undefined' && performance.now ? performance.now() : Date.now())

/* ---------------------------------------------------------------- tampon -- */

let buffer = []
try {
  const raw = sessionStorage.getItem(SS_KEY)
  if (raw) buffer = JSON.parse(raw).slice(-BUFFER_MAX)
} catch { /* private mode / kota — tampon boş başlar */ }

function persist() {
  try { sessionStorage.setItem(SS_KEY, JSON.stringify(buffer)) } catch { /* noop */ }
}

/* --------------------------------------------------------------- ayrıntı -- */

/** Ayrıntılı mod: localStorage bayrağı veya URL'de ?netlog */
function isVerbose() {
  try {
    if (localStorage.getItem('vertex_netlog_verbose') === '1') return true
    return new URLSearchParams(window.location.search).has('netlog')
  } catch { return false }
}

/* ----------------------------------------------------------- ortam bilgisi -- */

/** UA'dan kısa "Windows 10 · Chrome 126" etiketi — hangi makine olduğu tek bakışta görünsün. */
function uaShort(ua = '') {
  let os = 'Bilinmeyen OS'
  if (/Windows NT 10/.test(ua)) os = 'Windows 10/11'
  else if (/Windows NT ([\d.]+)/.test(ua)) os = `Windows NT ${RegExp.$1}`
  else if (/Mac OS X ([\d_]+)/.test(ua)) os = `macOS ${RegExp.$1.replace(/_/g, '.')}`
  else if (/Android/.test(ua)) os = 'Android'
  else if (/iPhone|iPad/.test(ua)) os = 'iOS'
  else if (/Linux/.test(ua)) os = 'Linux'

  let br = 'Bilinmeyen tarayıcı'
  const m =
    /Edg\/([\d.]+)/.exec(ua) ? ['Edge', RegExp.$1] :
    /OPR\/([\d.]+)/.exec(ua) ? ['Opera', RegExp.$1] :
    /Chrome\/([\d.]+)/.exec(ua) ? ['Chrome', RegExp.$1] :
    /Firefox\/([\d.]+)/.exec(ua) ? ['Firefox', RegExp.$1] :
    /Version\/([\d.]+).*Safari/.exec(ua) ? ['Safari', RegExp.$1] : null
  if (m) br = `${m[0]} ${String(m[1]).split('.')[0]}`
  return `${os} · ${br}`
}

function env() {
  const ua = typeof navigator !== 'undefined' ? navigator.userAgent : ''
  return {
    ua,
    ua_short: uaShort(ua),
    online: typeof navigator !== 'undefined' ? navigator.onLine : null,
    app_version: typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : 'dev',
  }
}

/* ------------------------------------------------------------- sınıflama -- */

/**
 * Hatayı türüne ayır. Teşhis için KRİTİK ayrım: "timeout/network" (istek sunucuya
 * hiç ulaşmadı ya da yanıt gelmedi) ile "http" (sunucu yanıt verdi, iş hatası)
 * bambaşka sorunlardır; Windows vakası birincisidir.
 */
function classify(error) {
  if (error?.code === 'ERR_CANCELED' || error?.name === 'CanceledError' || error?.__CANCEL__) return 'canceled'
  if (error?.code === 'ECONNABORTED' || error?.code === 'ETIMEDOUT' || /timeout/i.test(error?.message || '')) return 'timeout'
  if (error?.response) return 'http'
  if (error?.request) return 'network'   // istek gitti, yanıt yok (DNS/proxy/TLS/kopan bağlantı)
  return 'unknown'
}

const KIND_LABEL = {
  timeout: 'ZAMAN AŞIMI',
  network: 'AĞ HATASI',
  http: 'HTTP HATASI',
  canceled: 'İPTAL',
  slow: 'YAVAŞ',
  unknown: 'BİLİNMEYEN',
}

/* ---------------------------------------------------------------- kayıt --- */

function endpointOf(config) {
  const method = (config?.method || 'get').toUpperCase()
  const url = config?.url || '(bilinmeyen)'
  return { method, url, endpoint: `${method} ${url}` }
}

function elapsedOf(config) {
  const started = config?.metadata?.startedAt
  return typeof started === 'number' ? Math.round(now() - started) : null
}

function record(entry) {
  buffer.push(entry)
  if (buffer.length > BUFFER_MAX) buffer = buffer.slice(-BUFFER_MAX)
  persist()
}

function line(e) {
  const secs = e.elapsed_ms != null ? `${(e.elapsed_ms / 1000).toFixed(1)} sn` : '? sn'
  const limit = e.timeout_ms ? ` / limit ${(e.timeout_ms / 1000).toFixed(0)} sn` : ''
  const status = e.status ? ` · HTTP ${e.status}` : ''
  const code = e.code ? ` · code=${e.code}` : ''
  return `[NET] ${KIND_LABEL[e.kind] || e.kind} · ${e.endpoint} · ${secs}${limit}${status}${code}`
}

function toConsole(e) {
  const fn = e.kind === 'http' || e.kind === 'slow' ? console.warn : console.error
  fn(line(e))
  // Ayrıntı ikinci satırda: konsol kopyalanınca tek bakışta ortam da görünsün.
  console.log('       ↳', {
    zaman: e.ts,
    süre_ms: e.elapsed_ms,
    timeout_ms: e.timeout_ms,
    code: e.code,
    message: e.message,
    endpoint: e.endpoint,
    params: e.params,
    status: e.status,
    tarayıcı: e.ua_short,
    online: e.online,
    sürüm: e.app_version,
    retry: e.retry,
    user_agent: e.ua,
  })
}

/* ---------------------------------------------------------------- toast --- */

const lastToastAt = new Map()

function maybeToast(e) {
  // Ağ katmanı hataları HER ZAMAN görünür (kullanıcı "ekran takıldı" sanmasın).
  // HTTP hatalarını sayfalar zaten kendi toast'ıyla gösteriyor → yalnız ayrıntılı modda.
  if (e.kind === 'canceled' || e.kind === 'slow') return
  if (e.kind === 'http' && !isVerbose()) return
  // Arka plan poll'ları (header rozeti vb.) timeout olsa bile ekranı kirletmesin —
  // kullanıcı "uygulama phase out oldu" sanıyor; asıl iş kuyruğu tıkanmasıdır.
  if (e.silent) return
  // Güvenlik ağı: bilinen global poll path'leri (flag unutulursa bile toast basma)
  if (isBackgroundPollEndpoint(e.endpoint) && (e.kind === 'timeout' || e.kind === 'network')) return

  const key = `${e.kind}|${e.endpoint}`
  const prev = lastToastAt.get(key) || 0
  if (Date.now() - prev < TOAST_DEDUPE_MS) return   // paralel istekler tek toast'a insin
  lastToastAt.set(key, Date.now())

  const secs = e.elapsed_ms != null ? `${(e.elapsed_ms / 1000).toFixed(1)} sn` : '?'
  const limit = e.timeout_ms ? ` (limit ${(e.timeout_ms / 1000).toFixed(0)} sn)` : ''
  const title =
    e.kind === 'timeout' ? 'İstek zaman aşımına uğradı'
      : e.kind === 'network' ? 'Sunucuya ulaşılamadı'
        : e.kind === 'http' ? `Sunucu hatası (HTTP ${e.status})`
          : 'Ağ hatası'

  const detail = e.kind === 'http' ? apiErrorMessage(e._error) : (e.message || '')
  toast.error(
    title,
    `${e.endpoint}\n${secs}${limit} · ${e.code || '—'}\n${e.ua_short}${detail ? `\n${detail}` : ''}` +
    `\nAyrıntı için konsola __vertexNet.text() yazın`,
  )
}

/* ------------------------------------------------------------------ API --- */

/** İstek interceptor'ından çağrılır: başlangıç anını damgalar. */
export function markRequestStart(config) {
  config.metadata = { startedAt: now(), startedIso: new Date().toISOString() }
  return config
}

/** Yanıt hata interceptor'ından çağrılır: kaydı üretir, konsola ve ekrana basar. */
export function logRequestFailure(error) {
  const config = error?.config || {}
  const kind = classify(error)

  // İptaller normaldir (debounce'lu aramada her tuşta bir istek iptal olur) —
  // ayrıntılı mod kapalıyken tamponu kirletmesin.
  if (kind === 'canceled' && !isVerbose()) return

  const { method, url, endpoint } = endpointOf(config)
  const entry = {
    ts: new Date().toISOString(),
    kind,
    method,
    url,
    endpoint,
    params: config.params || null,
    elapsed_ms: elapsedOf(config),
    timeout_ms: config.timeout ?? null,
    code: error?.code || null,
    message: error?.message || null,
    status: error?.response?.status ?? null,
    status_text: error?.response?.statusText || null,
    retry: Boolean(config._retry),
    ...env(),
  }
  record(entry)
  toConsole(entry)
  maybeToast({ ...entry, _error: error, silent: Boolean(config.silentNetToast) })
  return entry
}

/** Başarılı ama anormal yavaş yanıtları da kaydeder (timeout'a ramak kalanlar). */
export function logSlowResponse(response) {
  const ms = elapsedOf(response?.config)
  if (ms == null || ms < SLOW_MS) return
  const { method, url, endpoint } = endpointOf(response.config)
  const entry = {
    ts: new Date().toISOString(),
    kind: 'slow',
    method,
    url,
    endpoint,
    params: response.config?.params || null,
    elapsed_ms: ms,
    timeout_ms: response.config?.timeout ?? null,
    code: null,
    message: null,
    status: response.status ?? null,
    status_text: response.statusText || null,
    retry: false,
    ...env(),
  }
  record(entry)
  toConsole(entry)
}

/* ------------------------------------------------- konsol yardımcı arayüzü -- */

function reportText() {
  const head = [
    `VERTEX ağ hata raporu — ${new Date().toLocaleString('tr-TR')}`,
    `Sürüm: ${env().app_version} · ${env().ua_short}`,
    `UA: ${env().ua}`,
    `Adres: ${window.location.href}`,
    `Kayıt: ${buffer.length}`,
    '',
  ]
  const rows = buffer.map((e) => `${e.ts} ${line(e)}${e.message ? ` · ${e.message}` : ''}`)
  return head.concat(rows.length ? rows : ['(kayıt yok)']).join('\n')
}

if (typeof window !== 'undefined' && !window.__vertexNet) {
  window.__vertexNet = {
    get entries() { return buffer.slice() },
    dump: () => {
      if (!buffer.length) { console.log('[NET] kayıt yok'); return }
      console.table(buffer.map((e) => ({
        zaman: e.ts, tür: e.kind, endpoint: e.endpoint, süre_ms: e.elapsed_ms,
        timeout_ms: e.timeout_ms, code: e.code, status: e.status, tarayıcı: e.ua_short,
      })))
    },
    text: () => { const t = reportText(); console.log(t); return t },
    copy: async () => {
      const t = reportText()
      try { await navigator.clipboard.writeText(t); console.log('[NET] panoya kopyalandı'); }
      catch { console.log('[NET] pano kapalı — metin yukarıda, elle kopyalayın'); console.log(t) }
      return t
    },
    clear: () => { buffer = []; persist(); console.log('[NET] tampon temizlendi') },
    enable: () => { try { localStorage.setItem('vertex_netlog_verbose', '1') } catch { /* noop */ }
      console.log('[NET] ayrıntılı mod AÇIK (HTTP hataları da toast olarak görünür)') },
    disable: () => { try { localStorage.removeItem('vertex_netlog_verbose') } catch { /* noop */ }
      console.log('[NET] ayrıntılı mod kapalı') },
    help: () => console.log(
      'dump() tablo · text() metin rapor · copy() panoya kopyala · enable()/disable() ayrıntılı mod · clear() temizle',
    ),
  }
}

export default { markRequestStart, logRequestFailure, logSlowResponse }
